<template>
  <div class="product-configurator" :class="{ 'product-configurator--preview': preview }">
    <div class="product-configurator__header">
      <img v-if="productImage" :src="productImage" alt="" class="product-configurator__image" />
      <div class="product-configurator__heading">
        <h3 class="product-configurator__title">{{ productTitle }}</h3>
        <p v-if="productSku" class="product-configurator__sku">{{ productSku }}</p>
      </div>
    </div>

    <div class="product-configurator__body">
      <section v-for="section in sections" :key="section.id" class="product-configurator__section">
        <h4 v-if="section.name" class="product-configurator__section-title">
          {{ section.name }}
        </h4>

        <div class="product-configurator__fields">
          <div
            v-for="option in section.options"
            :key="option.id"
            class="product-configurator__field"
            :class="{ 'product-configurator__field--wide': isWide(option) }"
          >
            <div
              v-if="option.type === 'notice'"
              class="product-configurator__notice"
              :class="`product-configurator__notice--${noticeVariant(option)}`"
            >
              <div class="product-configurator__notice-content" v-html="noticeContent(option)" />
            </div>

            <NumberInput
              v-else-if="option.type === 'number'"
              :id="fieldId(option)"
              :modelValue="optionValues[fieldId(option)]"
              :label="fieldLabel(option)"
              :info="option.caption || ''"
              :min="numberRange(option).min"
              :max="numberRange(option).max"
              :step="option.numberConfig?.step || 1"
              @update:modelValue="setOptionValue(fieldId(option), $event)"
            />

            <div v-else-if="option.type === 'single-choice' && usesChips(option)">
              <span class="product-configurator__chips-label">{{ fieldLabel(option) }}</span>
              <div class="product-configurator__chips" role="radiogroup">
                <button
                  v-for="choice in choiceOptions(option)"
                  :key="choice.value"
                  type="button"
                  role="radio"
                  class="product-configurator__chip"
                  :class="{
                    'product-configurator__chip--active':
                      optionValues[fieldId(option)] === choice.value,
                  }"
                  :aria-checked="optionValues[fieldId(option)] === choice.value"
                  :disabled="choice.disabled"
                  :title="choice.disabled ? $t('proposals.configurator.choice_unavailable') : ''"
                  @click="setOptionValue(fieldId(option), choice.value)"
                >
                  {{ choice.label }}
                </button>
              </div>
            </div>

            <SelectInput
              v-else-if="option.type === 'single-choice'"
              :id="fieldId(option)"
              :modelValue="optionValues[fieldId(option)] ?? ''"
              :label="fieldLabel(option)"
              :options="choiceOptions(option)"
              @update:modelValue="setOptionValue(fieldId(option), $event)"
            />

            <MultiSelectInput
              v-else-if="option.type === 'multi-choice'"
              :id="fieldId(option)"
              :modelValue="optionValues[fieldId(option)] || []"
              :label="fieldLabel(option)"
              :options="choiceOptions(option).filter((choice) => !choice.disabled)"
              :placeholder="$t('proposals.configurator.select_options')"
              @update:modelValue="setOptionValue(fieldId(option), $event)"
            />

            <ToggleInput
              v-else-if="option.type === 'toggle'"
              :id="fieldId(option)"
              :modelValue="!!optionValues[fieldId(option)]"
              :label="fieldLabel(option)"
              @update:modelValue="setOptionValue(fieldId(option), $event)"
            />

            <TextInput
              v-else-if="option.type === 'text'"
              :id="fieldId(option)"
              :modelValue="optionValues[fieldId(option)]"
              :label="fieldLabel(option)"
              @update:modelValue="setOptionValue(fieldId(option), $event)"
            />
          </div>
        </div>
      </section>

      <p v-if="!sections.length" class="product-configurator__empty">
        {{ $t('proposals.configurator.no_options') }}
      </p>
    </div>

    <div class="product-configurator__footer">
      <div class="product-configurator__price" aria-live="polite">
        <template v-if="rangeError">
          <span class="product-configurator__price-error">
            {{ $t('proposals.configurator.measure_out_of_range', rangeError) }}
          </span>
        </template>
        <template v-else-if="quote.error">
          <span class="product-configurator__price-error">{{ quote.error }}</span>
        </template>
        <template v-else-if="missingRequired">
          <span class="product-configurator__price-label">
            {{ $t('proposals.configurator.missing_required', { count: missingRequired }) }}
          </span>
        </template>
        <template v-else>
          <span class="product-configurator__price-label">
            {{ $t('proposals.configurator.price_incl') }}
          </span>
          <strong v-if="!quote.loading && quote.total != null">
            {{ formatCurrency(quote.total) }}
          </strong>
          <span v-else class="product-configurator__price-pending">
            {{ $t('proposals.configurator.calculating') }}
          </span>
        </template>
      </div>
      <div v-if="!preview" class="product-configurator__actions">
        <Btn variant="outlined" type="button" :text="cancelLabel" @click="$emit('cancel')" />
        <Btn
          variant="primary"
          type="button"
          :text="submitLabel"
          :disabled="!canSubmit"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from 'vue';
  import { pricingService } from '@/services/pricingService';
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import MultiSelectInput from '@/components/inputs/MultiSelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import { resolveConstraintDimensionValue } from '@shared/composables/useCPQPriceCalculator';
  import { evaluateCPQOptionConditions } from '@shared/utils/conditionsEvaluator';
  import { getDisabledChoices } from '@shared/utils/choiceAvailability';
  import { useMatrixPricingValidation } from '@shared/composables/useMatrixPricingValidation';
  import { priceConfiguredProduct } from '@shared/utils/configuredProductPricing';
  import {
    buildProductAutoFillGroups,
    productChoicesByFieldId,
    resolveAutoFillPatch,
  } from '@shared/utils/autoFill';

  const CONTENT_ONLY_TYPES = ['notice', 'image', 'photo'];
  const CHIP_MAX_CHOICES = 4;
  const CHIP_MAX_LABEL = 16;

  /**
   * Configure one catalog product inline, on the dealer channel: same fields
   * as the measuring form (externalOnly stays hidden). Applies option
   * conditions, choice constraints, per-choice conditions and auto-fill, and
   * prices one piece in the browser with the same calculator.
   */
  export default {
    name: 'ProductConfiguratorForm',

    components: { TextInput, NumberInput, SelectInput, MultiSelectInput, ToggleInput, Btn },

    props: {
      product: { type: Object, required: true },
      initialConfiguration: { type: Object, default: null },
      submitLabel: { type: String, default: '' },
      cancelLabel: { type: String, default: '' },
      preview: { type: Boolean, default: false },
      audience: { type: String, default: 'dealer' },
    },

    emits: ['submit', 'cancel'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const productTitle = computed(
        () => props.product?.title || props.product?.name || $t('proposals.configurator.product')
      );
      const productSku = computed(() => props.product?.sku || props.product?.base_sku || '');
      const productImage = computed(
        () => props.product?.imageUrl || props.product?.image_url || null
      );

      const fieldId = (option) => `${props.product.id}-${option.id}-input`;

      const defaultValue = (option) => {
        switch (option.type) {
          case 'number':
            return option.numberConfig?.defaultValue ?? '';
          case 'single-choice': {
            const choices = option.choiceConfig?.choices || [];
            return (choices.find((c) => c.isDefault) || choices[0])?.id ?? null;
          }
          case 'multi-choice':
            return [];
          case 'toggle':
            return option.toggleConfig?.defaultValue ?? false;
          case 'text':
            return '';
          default:
            return null;
        }
      };

      const buildInitialValues = () => {
        const values = {};
        (props.product.options || []).forEach((option) => {
          if (CONTENT_ONLY_TYPES.includes(option.type)) return;
          const key = fieldId(option);
          const initial = props.initialConfiguration?.[key];
          values[key] = initial !== undefined ? initial : defaultValue(option);
        });
        return values;
      };

      const optionValues = reactive(buildInitialValues());

      watch(
        () => (props.product?.options || []).map((option) => option.id).join('\n'),
        () => {
          if (!props.preview) return;
          for (const option of props.product?.options || []) {
            if (CONTENT_ONLY_TYPES.includes(option.type)) continue;
            const key = fieldId(option);
            if (optionValues[key] === undefined) optionValues[key] = defaultValue(option);
          }
        }
      );
      // Selling-price product. Preview prices the draft in place; otherwise
      // the public configurator product is loaded on mount.
      const configuratorProduct = ref(props.preview ? props.product : null);

      const attributeValues = computed(() => ({
        'selected-product': props.product,
        'product-selection-element': props.product.id,
        ...optionValues,
      }));

      // Dealer channel hides consumer-only fields. The catalog preview shows
      // the customer form, so internal measuring fields stay out instead.
      const channelHidden = (field) =>
        props.audience === 'customer' ? field?.internalOnly : field?.externalOnly;

      const isOptionVisible = (option) => {
        if (channelHidden(option)) return false;
        if (CONTENT_ONLY_TYPES.includes(option.type)) return option.type === 'notice';
        return evaluateCPQOptionConditions(option.conditions, attributeValues.value, {
          resolveFieldId: (id) => `${props.product.id}-${id}-input`,
        });
      };

      const visibleOptions = computed(() =>
        (props.product.options || []).filter((option) => isOptionVisible(option))
      );

      const disabledChoices = computed(() =>
        getDisabledChoices(props.product, optionValues, {
          resolveNumericDimension: resolveConstraintDimensionValue,
        })
      );

      const dealerChoices = (option) =>
        (option.choiceConfig?.choices || []).filter((choice) => !channelHidden(choice));

      const matrixProduct = computed(() => {
        const source = configuratorProduct.value || props.product;
        if (!source) return null;
        const pricingMatrix = source.pricingMatrix || source.pricing_matrix || null;
        const useMatrixPricing = Boolean(source.useMatrixPricing || source.use_matrix_pricing);
        if (!useMatrixPricing || !pricingMatrix) return null;
        return { ...source, useMatrixPricing, pricingMatrix };
      });

      const matrixValidation = computed(() =>
        matrixProduct.value ? useMatrixPricingValidation(matrixProduct.value, optionValues) : null
      );

      const matrixChoiceAvailable = (choice, availableValues) => {
        if (!availableValues.length) return true;
        const label = choice.label || '';
        if (
          availableValues.includes(label) ||
          availableValues.includes(choice.id) ||
          availableValues.includes(choice.content)
        ) {
          return true;
        }
        return availableValues.some((available) => {
          const avStr = String(available).trim();
          const labelStr = String(label).trim();
          const contentStr = String(choice.content || '').trim();
          return (
            labelStr.startsWith(avStr) ||
            contentStr.startsWith(avStr) ||
            avStr.startsWith(labelStr) ||
            (contentStr && avStr.startsWith(contentStr))
          );
        });
      };

      // Choice constraints plus sizes the price matrix does not contain.
      const blockedChoices = computed(() => {
        const merged = new Map();
        disabledChoices.value.forEach((ids, optionId) => merged.set(optionId, new Set(ids)));
        const validation = matrixValidation.value;
        if (!validation) return merged;
        (props.product.options || []).forEach((option) => {
          if (option.type !== 'single-choice' && option.type !== 'multi-choice') return;
          const available = validation.getAvailableValues(option.id);
          if (!available.length) return;
          dealerChoices(option).forEach((choice) => {
            if (matrixChoiceAvailable(choice, available)) return;
            if (!merged.has(option.id)) merged.set(option.id, new Set());
            merged.get(option.id).add(choice.id);
          });
        });
        return merged;
      });

      const numberRange = (option) => {
        const configuredMin = parseFloat(option.numberConfig?.min);
        const configuredMax = parseFloat(option.numberConfig?.max);
        const min = Number.isFinite(configuredMin) ? configuredMin : 0;
        const fallbackMax = Number.isFinite(configuredMax) ? configuredMax : 100;
        const matrixMax = matrixValidation.value?.getNumberMax(option.id) ?? null;
        if (matrixMax === null) return { min, max: fallbackMax };
        return { min, max: Math.min(fallbackMax, matrixMax) };
      };

      // Same as the measuring form: a selection that becomes impossible moves to
      // the first allowed choice.
      watch(
        blockedChoices,
        (disabled) => {
          (props.product.options || []).forEach((option) => {
            if (option.type !== 'single-choice' && option.type !== 'multi-choice') return;
            const ids = disabled.get(option.id) || new Set();
            const key = fieldId(option);
            const selectable = dealerChoices(option);
            if (option.type === 'multi-choice') {
              const current = optionValues[key] || [];
              const next = current.filter(
                (id) => !ids.has(id) && selectable.some((choice) => choice.id === id)
              );
              if (next.length !== current.length) optionValues[key] = next;
              return;
            }
            const current = optionValues[key];
            if (current === undefined || current === null || current === '') return;
            const stillAllowed =
              selectable.some((choice) => choice.id === current) && !ids.has(current);
            if (stillAllowed) return;
            const next = selectable.find((choice) => !ids.has(choice.id));
            if (next) optionValues[key] = next.id;
          });
        },
        { immediate: true }
      );

      const groups = computed(
        () => props.product.optionGroups || props.product.option_groups || []
      );

      const sections = computed(() => {
        const byGroup = new Map();
        const ungrouped = [];
        visibleOptions.value.forEach((option) => {
          const groupId = option.pageGroupId || option.page_group_id || null;
          if (!groupId || !groups.value.some((g) => g.id === groupId)) {
            ungrouped.push(option);
            return;
          }
          if (!byGroup.has(groupId)) byGroup.set(groupId, []);
          byGroup.get(groupId).push(option);
        });

        const result = [];
        if (ungrouped.length) result.push({ id: 'ungrouped', name: '', options: ungrouped });
        groups.value.forEach((group) => {
          const options = byGroup.get(group.id);
          if (options?.length) {
            result.push({ id: group.id, name: group.name || group.label || '', options });
          }
        });
        return result;
      });

      const choiceOptions = (option) => {
        const disabled = blockedChoices.value.get(option.id);
        return dealerChoices(option).map((choice) => ({
          value: choice.id,
          label: choice.label || choice.id,
          disabled: Boolean(disabled?.has(choice.id)),
        }));
      };

      const usesChips = (option) => {
        const choices = dealerChoices(option);
        return (
          choices.length > 0 &&
          choices.length <= CHIP_MAX_CHOICES &&
          choices.every((c) => (c.label || '').length <= CHIP_MAX_LABEL)
        );
      };

      const isWide = (option) =>
        option.type === 'notice' ||
        option.type === 'multi-choice' ||
        (option.type === 'text' && option.width === 'full');

      const fieldLabel = (option) => (option.required ? `${option.label} *` : option.label);

      const isEmpty = (value) =>
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      const missingRequired = computed(
        () =>
          visibleOptions.value.filter(
            (option) =>
              option.required &&
              !CONTENT_ONLY_TYPES.includes(option.type) &&
              option.type !== 'toggle' &&
              isEmpty(optionValues[fieldId(option)])
          ).length
      );

      const autoFilledFieldIds = ref(new Set());
      const choicesByFieldId = computed(() => productChoicesByFieldId(props.product));
      const autoFillGroupByField = computed(() => {
        const map = {};
        buildProductAutoFillGroups(props.product).forEach((group) => {
          group.fieldIds.forEach((id) => {
            map[id] = group;
          });
        });
        return map;
      });

      const setOptionValue = (key, value) => {
        const group = autoFillGroupByField.value[key];
        const seeded = group
          ? resolveAutoFillPatch({
              changedFieldId: key,
              value,
              group,
              choicesById: choicesByFieldId.value,
              currentValues: optionValues,
              seededFieldIds: autoFilledFieldIds.value,
            })
          : {};
        optionValues[key] = value;
        const nextSeeded = new Set(autoFilledFieldIds.value);
        nextSeeded.delete(key);
        Object.entries(seeded).forEach(([seededFieldId, choiceId]) => {
          optionValues[seededFieldId] = choiceId;
          nextSeeded.add(seededFieldId);
        });
        autoFilledFieldIds.value = nextSeeded;
      };

      // Visible dealer fields, plus a consumer value already stored on the line.
      // The price formula still falls back to that hidden height.
      const currentConfiguration = () => {
        const visibleKeys = new Set(visibleOptions.value.map((option) => fieldId(option)));
        return Object.fromEntries(
          Object.entries(optionValues).filter(([key, value]) => {
            if (visibleKeys.has(key)) return true;
            const option = (props.product.options || []).find((item) => fieldId(item) === key);
            return channelHidden(option) === true && !isEmpty(value);
          })
        );
      };

      // The dashboard catalog holds cost prices, so pricing runs on the
      // configurator product (selling prices) the public forms also get.
      const productLoad = reactive({ loading: !props.preview, error: '' });

      watch(
        () => props.product,
        (product) => {
          if (props.preview) configuratorProduct.value = product;
        }
      );

      onMounted(async () => {
        if (props.preview) return;
        try {
          configuratorProduct.value = await pricingService.getConfiguratorProduct(props.product.id);
        } catch (error) {
          productLoad.error =
            error?.response?.data?.error || $t('proposals.configurator.price_failed');
        } finally {
          productLoad.loading = false;
        }
      });

      const browserPricing = computed(() => {
        if (!configuratorProduct.value || missingRequired.value) return null;
        try {
          return priceConfiguredProduct(configuratorProduct.value, currentConfiguration()).pricing;
        } catch (error) {
          console.error('[ProductConfiguratorForm] Pricing failed', error);
          return null;
        }
      });

      const quote = computed(() => {
        if (productLoad.error) return { loading: false, total: null, error: productLoad.error };
        if (productLoad.loading) return { loading: true, total: null, error: '' };
        if (missingRequired.value) return { loading: false, total: null, error: '' };
        if (!browserPricing.value) {
          return { loading: false, total: null, error: $t('proposals.configurator.price_failed') };
        }
        const unitPrice = Number(browserPricing.value.totalPrice) || 0;
        const vatRate = Number(configuratorProduct.value.vatRate ?? 21);
        const total = configuratorProduct.value.pricesIncludeVat
          ? unitPrice
          : unitPrice * (1 + vatRate / 100);
        return { loading: false, total: Math.round(total * 100) / 100, error: '' };
      });

      const rangeError = computed(() => {
        const option = visibleOptions.value.find((item) => {
          if (item.type !== 'number') return false;
          const raw = optionValues[fieldId(item)];
          if (raw === '' || raw === null || raw === undefined) return false;
          const value = Number(raw);
          if (!Number.isFinite(value)) return false;
          const { min, max } = numberRange(item);
          return value < min || value > max;
        });
        if (!option) return null;
        const { min, max } = numberRange(option);
        return { label: option.label, min, max };
      });

      const canSubmit = computed(
        () =>
          !missingRequired.value &&
          !rangeError.value &&
          !quote.value.loading &&
          !quote.value.error &&
          quote.value.total != null
      );

      const displayValue = (option, value) => {
        const choices = option.choiceConfig?.choices || [];
        if (option.type === 'single-choice') {
          return choices.find((c) => c.id === value)?.label ?? value;
        }
        if (option.type === 'multi-choice' && Array.isArray(value)) {
          return value.map((id) => choices.find((c) => c.id === id)?.label ?? id).join(', ');
        }
        if (option.type === 'toggle') {
          const on = value === true || value === 'true';
          return on ? option.toggleConfig?.onLabel || 'Ja' : option.toggleConfig?.offLabel || 'Nee';
        }
        return value;
      };

      const escapeHtml = (value) =>
        String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      const noticeVariant = (option) => option.noticeConfig?.variant || 'info';

      const noticeContent = (option) => {
        const content = option.noticeConfig?.content || '';
        if (!content) return escapeHtml(option.label || '');
        return content.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, key) => {
          const referenced = (props.product.options || []).find((item) => item.id === key.trim());
          if (!referenced) return '';
          const value = optionValues[fieldId(referenced)];
          if (isEmpty(value)) return '';
          return escapeHtml(displayValue(referenced, value));
        });
      };

      // Printed verbatim on the quote PDF, so options hidden from the customer stay out.
      const configurationSummary = () =>
        visibleOptions.value
          .filter((option) => !CONTENT_ONLY_TYPES.includes(option.type))
          .filter((option) => !option.hideOnQuote && !option.internalOnly)
          .filter((option) => !isEmpty(optionValues[fieldId(option)]))
          .map(
            (option) => `${option.label}: ${displayValue(option, optionValues[fieldId(option)])}`
          )
          .join('\n');

      const handleSubmit = () => {
        if (!canSubmit.value) return;
        emit('submit', {
          configuration: currentConfiguration(),
          detailsSummary: configurationSummary(),
          unitTotal: quote.value.total,
          pricing: browserPricing.value,
        });
      };

      const formatCurrency = (amount) =>
        new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
          Number(amount) || 0
        );

      return {
        productTitle,
        productSku,
        productImage,
        sections,
        optionValues,
        fieldId,
        fieldLabel,
        choiceOptions,
        usesChips,
        isWide,
        noticeVariant,
        noticeContent,
        setOptionValue,
        missingRequired,
        rangeError,
        numberRange,
        quote,
        canSubmit,
        handleSubmit,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  .product-configurator {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;

    &--preview {
      height: auto;

      .product-configurator__body {
        overflow: visible;
      }

      .product-configurator__fields {
        grid-template-columns: 1fr;
      }
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      padding-bottom: 1rem;
    }

    &__image {
      width: 3.5rem;
      height: 3.5rem;
      object-fit: cover;
      border-radius: 0.5rem;
      background: var(--slate-100);
    }

    &__title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__sku {
      margin: 0.125rem 0 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 1.75rem;
      padding: 0.25rem 0.25rem 1.5rem 0;
    }

    &__section-title {
      margin: 0 0 0.75rem;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--slate-500);
    }

    &__fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0.25rem 1rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__field--wide {
      grid-column: 1 / -1;
    }

    &__notice {
      margin-bottom: 0.75rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      background: var(--slate-50);
    }

    &__notice-content {
      font-size: 0.875rem;
      color: var(--slate-700);

      :deep(p) {
        margin: 0;
      }

      :deep(p + p) {
        margin-top: 0.25rem;
      }
    }

    &__chips-label {
      display: block;
      margin-bottom: 0.375rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
    }

    &__chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      margin-bottom: 1rem;
    }

    &__chip {
      padding: 0.4375rem 0.875rem;
      border: 1px solid var(--slate-200);
      border-radius: 999px;
      background: white;
      font-size: 0.875rem;
      color: var(--slate-700);
      cursor: pointer;
      transition:
        background-color 0.15s ease-out,
        border-color 0.15s ease-out,
        color 0.15s ease-out;

      @include hover {
        border-color: var(--slate-300);
      }

      &--active {
        border-color: var(--brand-600);
        background: var(--brand-50);
        color: var(--brand-700);
        font-weight: 500;
      }

      &:disabled {
        cursor: not-allowed;
        color: var(--slate-400);
        background: var(--slate-50);
        text-decoration: line-through;
      }

      &:focus-visible {
        outline: 2px solid var(--brand-500);
        outline-offset: 2px;
      }
    }

    &__empty {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
    }

    &__price {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;

      strong {
        font-size: 1.125rem;
        font-variant-numeric: tabular-nums;
        color: var(--slate-900);
      }
    }

    &__price-label,
    &__price-pending {
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__price-error {
      font-size: 0.8125rem;
      color: var(--red-600);
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
    }
  }
</style>
